import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { BlobType, DatasetFileTypeOption, ErrorNotificationMode, FileModel, UploadFileItem } from '@ca/core/models';
import { BreakpointObserverService, FileUploadService } from '@ca/core/services';
import { environment } from '@ca/environment/environment';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FileItem, FileUploader } from 'ng2-file-upload';
import { CreateFolder } from 'src/app/core/models/create-folder.model';

@UntilDestroy()
@Component({
    selector: 'ca-file-upload',
    templateUrl: './file-upload.component.html',
    styleUrls: ['./file-upload.component.scss'],
})
export class FileUploadComponent implements OnInit, AfterViewInit {
    @Input() isDisabled: boolean = false;
    @Input() allowMultipleFiles: boolean = true;
    @Input() showHeader: boolean = false;
    @Input() maxInputSizeInBytes: number = environment.defaultMaxFileSizeInBytes;
    @Input() showGeneralMaxInputSizeMessage: boolean = true;
    @Input() supportedFileTypes: string[] | undefined;
    @Input() notSupportedFileTypeCustomMessage: string | undefined;
    @Input() errorNotificationMode: ErrorNotificationMode = 'none';
    @Input() uploadValidationSelectedFiles: FileModel[] = [];

    // Provide a unique key in order to allow saving the state ("cache")
    @Input() uploaderKey: string;

    // Pass URL from outside if it needs to be different than 'documents/upload/uploadinfo'.
    // If null, default is used.
    @Input() blobType: BlobType = BlobType.UserFile;

    /**
     * Name of the file.
     * Only used for BlobType.WorkflowImage, BlobType.ModuleImage, BlobType.EmbeddedImages and BlobType.TestFile
     */
    @Input() pathToAppend?: string;
    /**
     * A base 64 string of an image which will automatically be uploaded to our blob storage. Currently used for pasting images
     * into Quill-Editor.
     */
    @Input() base64StringToUpload?: string;

    // If provided files will be uploaded to provided folder
    @Input() targetFolderId?: string;
    // If provided and targetFolderId is not provided, folder in provided
    // path will be created and files will be uploaded to it
    @Input() createTargetFolder?: CreateFolder;

    /**
     * If provided, will upload the file and connect it to given dataset
     */
    @Input() datasetId?: string;
    @Input() datasetFileType?: DatasetFileTypeOption;
    @Input() allowSameFileNames: boolean = true;

    @Output() onReset: EventEmitter<void> = new EventEmitter<void>();
    @Output() onFilesAdded: EventEmitter<UploadFileItem[]> = new EventEmitter<UploadFileItem[]>();
    @Output() onUploadStarted: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() onUploadCompleted: EventEmitter<FileModel> = new EventEmitter<FileModel>();
    @Output() onAllUploadsCompleted: EventEmitter<void> = new EventEmitter<void>();
    @Output() onFileCreated: EventEmitter<FileModel> = new EventEmitter<FileModel>();
    @Output() onUploadRemoved: EventEmitter<UploadFileItem> = new EventEmitter<UploadFileItem>();
    @Output() invalidFiles: EventEmitter<UploadFileItem[]> = new EventEmitter<UploadFileItem[]>();

    @ViewChild('uploadButton') uploadButton: ElementRef;

    // HTML Input (type=file) element
    @ViewChild('uploadSection') fileInput: HTMLInputElement;

    _hasBaseDropZoneOver: boolean = false;
    isSmallDevice: boolean = false;
    uploader: FileUploader | null;
    cancelUpload: FileItem;
    cancelAllUploads: { cancel: boolean };

    constructor(
        private breakpointObserverService: BreakpointObserverService,
        private fileUploadService: FileUploadService,
    ) {}

    ngOnInit(): void {
        this.breakpointObserverService.isSmallDevice$.pipe(untilDestroyed(this)).subscribe((isSmallDevice: boolean) => {
            this.isSmallDevice = isSmallDevice;
        });
    }

    ngAfterViewInit(): void {
        // uploader is already initialized by arivis Cloud-file-drop directive and stored in the cache
        if (this.uploaderKey) {
            // setTimeout to avoid expression has changed after it was checked exception
            setTimeout(() => {
                this.uploader = this.fileUploadService.fetchFromCache(this.uploaderKey!);
            });
        } else {
            console.error('FileUploadComponent - uploaderKey must be provided!');
        }
    }

    fileOverBase(e: boolean): void {
        this._hasBaseDropZoneOver = e;
    }

    isLoading(): boolean {
        return !this.uploader && this.blobType !== BlobType.UserFile;
    }

    getUploaderCssClasses(): Object {
        return {
            'nv-file-over': this._hasBaseDropZoneOver,
            'mobile-browse': this.isSmallDevice,
            'h-100': !this.uploader?.isUploading,
            'h-80': this.uploader?.isUploading && this.uploader?.queue.length === 1,
            'h-70': this.uploader?.isUploading && this.uploader?.queue.length >= 2,
        };
    }

    resetFileUploader(): void {
        this.onReset.emit();
    }

    filesAdded(files: UploadFileItem[]): void {
        this.onFilesAdded.emit(files);
    }

    uploadStarted(started: boolean): void {
        this.onUploadStarted.emit(started);
    }

    uploadCompleted(file: FileModel): void {
        this.onUploadCompleted.emit(file);
    }

    allUploadsCompleted(): void {
        this.onAllUploadsCompleted.emit();
    }

    fileCreated(file: FileModel): void {
        this.onFileCreated.emit(file);
    }

    uploadRemoved(file: UploadFileItem): void {
        this.onUploadRemoved.emit(file);
    }

    cancelFileUpload(file: FileItem): void {
        this.cancelUpload = file;
    }
}
